/* Service-worker registration.
 *
 * Registration is **PROD-only** so dev-mode HMR is never confused by a
 * cached bundle, and so a cold `vite dev` run mirrors a fresh browser load
 * with no SW interference. The registration is fire-and-forget — failures
 * are swallowed (with a console warn in dev tools) because an SW that fails
 * to install must not break the app.
 *
 * The SW itself lives at `sw.js` (under `public/`), is stamped with the
 * build hash by `scripts/stamp-sw.mjs`, and follows a strict same-origin
 * cache-first policy. See SECURITY.md → Offline-first SW.
 *
 * The privacy gate (scripts/check-network-calls.mjs) allowlists this file so
 * the bare `navigator.serviceWorker.register` call here does not trip the
 * off-device path detector.
 *
 * Dev-mode detection: rather than depend on `import.meta.env.PROD` (which
 * requires a Vite client-types reference and pulls a build-tool coupling
 * into our type roots), we sniff the runtime URL. The dev server runs over
 * `localhost` / `127.0.0.1` / a local IPv6 loopback; production deploys run
 * on a regular DNS hostname. The detector is defensively conservative:
 * anything that *looks* like a dev URL skips registration.
 *
 * Subpath deploys. The previous registration targeted the absolute path
 * `/sw.js`, which only works when the app is mounted at the document root.
 * On subpath deploys (e.g. `https://example.com/mime-smith/`) the SW
 * file lives at `…/mime-smith/sw.js` and registering against `/sw.js`
 * either 404s or, worse, attaches an SW from an unrelated scope. We now
 * derive both the script URL and the registration `scope` from
 * `document.baseURI` so the registration adapts to whatever base the deploy
 * lands at. `document.baseURI` returns a fully-resolved absolute URL from
 * any `<base href>` element (or `window.location.href` when none is present)
 * — so both the explicit-`<base>` and Vite's runtime default behave
 * correctly. We deliberately keep using `document.baseURI` instead of
 * `import.meta.env.BASE_URL` to avoid pulling the `vite/client` type
 * reference into the source tree (matches the same rationale documented
 * above for the dev-host sniffer). */

function looksLikeDevHost(): boolean {
  if (typeof window === "undefined") return true;
  const host = window.location.hostname;
  if (!host) return true;
  if (host === "localhost") return true;
  if (host === "127.0.0.1") return true;
  if (host === "::1") return true;
  if (host === "[::1]") return true;
  // *.local mDNS hosts and bare-IP private networks during dev.
  if (host.endsWith(".local")) return true;
  return false;
}

/**
 * Resolve the absolute URL to the SW script and the scope it should
 * control, anchored to the document base.
 *
 * `document.baseURI` is always an absolute URL (the parsing happens
 * inside the browser; even with no `<base>` element it falls back to
 * `window.location.href`). We strip any trailing query / fragment off
 * the base before composing the SW URL so query-string deep-links
 * don't break the registration. The base is also normalised to end
 * with `/` so the resolved `sw.js` lands as a sibling, not a suffix.
 *
 * Exported for the dedicated test in `sw-register.test.ts`.
 */
export function resolveSwRegistration(): { url: string; scope: string } {
  // Fall back to "/" when document is unavailable (SSR / pre-mount).
  const rawBase =
    typeof document !== "undefined" && typeof document.baseURI === "string"
      ? document.baseURI
      : "/";
  // Drop?query and #hash. The browser produces these as part of
  // `document.baseURI` in some edge cases (e.g. when no <base> element is
  // present and the page itself was loaded with a query).
  let base = rawBase.replace(/[?#].*$/, "");
  // Strip the trailing filename (anything after the last "/") so the
  // base is a directory path. `https://example.com/app/index.html`
  // becomes `https://example.com/app/`.
  const lastSlash = base.lastIndexOf("/");
  if (lastSlash >= 0) base = base.slice(0, lastSlash + 1);
  // Defensive: ensure trailing slash so URL composition treats `base`
  // as a directory.
  if (!base.endsWith("/")) base += "/";
  return { url: `${base}sw.js`, scope: base };
}

export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (looksLikeDevHost()) return;
  window.addEventListener("load", () => {
    const { url, scope } = resolveSwRegistration();
    navigator.serviceWorker.register(url, { scope }).catch((err) => {
      // Don't surface to the user — SW is an enhancement, not a
      // requirement. Log so a future dev-tools session catches it.
      console.warn("[mime-smith] service worker registration failed:", err);
    });
  });
}
